import { useMemo, useState } from "react";
import { Alert } from "react-bootstrap";
import { useHistory } from "react-router-dom";
import { useGetAllCategories } from "../../../../../api/jeep-rally/categories/categories.hooks";
import {
  useCreateRallyMutation,
  useCreateRallyPricingsMutation,
} from "../../../../../api/jeep-rally/rally/rally.hooks";
import { normalizeCategories } from "../../Categories/utils/category.utils";
import {
  buildRallyPayload,
  buildRallyPricingPayloads,
  getRallyEventId,
  initialRallyForm,
  validateRallyForm,
} from "../utils/rally.utils";
import RallyForm from "./_components/RallyForm";

const RallyCreatePage = () => {
  const history = useHistory();
  const [form, setForm] = useState(initialRallyForm);
  const [validationErrors, setValidationErrors] = useState({});
  const [submitError, setSubmitError] = useState("");

  const {
    mutateAsync: createRally,
    isLoading,
    isPending,
    reset,
  } = useCreateRallyMutation();

  const {
    mutateAsync: createPricings,
    isLoading: isPricingLoading,
    isPending: isPricingPending,
    reset: resetPricing,
  } = useCreateRallyPricingsMutation();

  const submitting =
    isLoading || isPending || isPricingLoading || isPricingPending;

  const {
    data: categoriesData,
    isLoading: isCategoriesLoading,
    isError: isCategoriesError,
  } = useGetAllCategories();
  const categories = useMemo(
    () => normalizeCategories(categoriesData),
    [categoriesData]
  );

  const updateField = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setValidationErrors((prev) => ({ ...prev, [key]: undefined }));
  };

  const handleHighlightChange = (index, value) => {
    setForm((prev) => {
      const highlights = [...(prev.highlights || [])];
      highlights[index] = value;
      return { ...prev, highlights };
    });
  };

  const handleAddHighlight = () => {
    setForm((prev) => ({
      ...prev,
      highlights: [...(prev.highlights || []), ""],
    }));
  };

  const handleRemoveHighlight = (index) => {
    setForm((prev) => {
      const highlights = [...(prev.highlights || [])];
      if (highlights.length <= 1) return { ...prev, highlights: [""] };
      highlights.splice(index, 1);
      return { ...prev, highlights };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    reset();
    resetPricing();
    setSubmitError("");

    const nextErrors = validateRallyForm(form);
    setValidationErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;

    let eventId = "";

    try {
      const createdRally = await createRally(buildRallyPayload(form));
      eventId = getRallyEventId(createdRally);

      if (!eventId) {
        throw new Error("Rally was created but no event id was returned.");
      }

      const prices = buildRallyPricingPayloads(form);
      if (prices.length) {
        await createPricings({ eventId, prices });
      }

      history.push("/jeep-rally/rallies");
    } catch (error) {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to create rally. Please try again.";

      if (eventId) {
        history.push(`/jeep-rally/rallies/edit/${eventId}`);
        return;
      }

      setSubmitError(message);
    }
  };

  const goBack = () => history.push("/jeep-rally/rallies");

  return (
    <div className="container-fluid">
      <div className="card">
        <div className="card-header d-flex justify-content-between align-items-center">
          <div className="d-flex align-items-center gap-2">
            <button
              type="button"
              className="btn btn-link p-0 text-decoration-none"
              onClick={goBack}
              aria-label="Back to rallies list"
              title="Back"
            >
              <i className="bi bi-arrow-left fs-4" />
            </button>
            <h4 className="card-title mb-0">Create Rally</h4>
          </div>
        </div>

        <div className="card-body">
          <p className="text-muted mb-4">
            Create a rally and assign one or more categories. Each selected
            category needs a price, which is saved after the rally is created.
          </p>

          {submitError && (
            <Alert variant="danger" className="mb-4">
              {submitError}
            </Alert>
          )}

          <RallyForm
            form={form}
            validationErrors={validationErrors}
            submitting={submitting}
            submitLabel="Create Rally"
            submittingLabel="Creating..."
            onSubmit={handleSubmit}
            onFieldChange={updateField}
            onHighlightChange={handleHighlightChange}
            onAddHighlight={handleAddHighlight}
            onRemoveHighlight={handleRemoveHighlight}
            onBack={goBack}
            categories={categories}
            isCategoriesLoading={isCategoriesLoading}
            isCategoriesError={isCategoriesError}
          />
        </div>
      </div>
    </div>
  );
};

export default RallyCreatePage;
